import { useRef } from 'react'
import { persistent } from '../constants/index.js'
import { useAppCtx, useContentQuery } from '../ctx.js'
import { useSelectedVotingRegion } from './useSelectedVotingRegion.js'
import { getVotingRegionName } from './useVotingRegion.js'
import { formatCurrency } from '../utils/formatCurrency.js'
import { PARTICIPATING_COUNTRIES } from '../../../shared/constants.js'

export const LOADING = {}

const MUSTACHE_TAG = /\{\{\s*([A-Za-z0-9_-]+)\s*\}\}/g

export const useDictionary = (): Record<string, string> => {
  const currentRef = useRef<Record<string, string>>(LOADING)
  const sourceRef = useRef<Record<string, string> | null>(null)
  const inputsRef = useRef<string>('')
  const { data, loading, error } = useContentQuery(
    'dictionary',
    null,
    persistent,
  )
  const {
    activeLanguage,
    votesCount = 0,
    minVotes = 1,
    maxVotes = 0,
    price = 0,
    currency,
  } = useAppCtx()
  const [regionCode] = useSelectedVotingRegion()

  const lang = activeLanguage || 'en'
  const cur = currency || 'eur'
  const regionName = getVotingRegionName(regionCode, lang)
  const isParticipating = (
    PARTICIPATING_COUNTRIES as readonly string[]
  ).includes(regionCode)

  const inputsKey = `${maxVotes}|${votesCount}|${minVotes}|${price}|${cur}|${lang}|${regionName}|${isParticipating}`

  if (
    !loading &&
    data &&
    (data !== sourceRef.current || inputsRef.current !== inputsKey)
  ) {
    sourceRef.current = data
    inputsRef.current = inputsKey

    const replacements: Record<string, string> = {
      YEAR: '2026',
      MAX_VOTES: String(maxVotes),
      IP_COUNTRY: isParticipating
        ? regionName
        : (data.nonParticipatingCountry as string) || '',
      VOTING_REGION:
        regionCode === 'ROW'
          ? (data.nonParticipatingCountry as string)
          : regionName,
      SINGLE_VOTE_PRICE_LABEL: formatCurrency(price / 100, cur, lang),
      // REMAINING_VOTES: String(maxVotes - votesCount), do this one for sms only on lastslide
      TOTAL_PRICE_LABEL: formatCurrency((price * votesCount) / 100, cur, lang),
      COUNT: String(votesCount),
      OVERSEAS: '',
    }

    const replaced: Record<string, string> = {}
    for (const key in data) {
      const v = data[key]
      replaced[key] =
        typeof v === 'string'
          ? v.replace(MUSTACHE_TAG, (match, name) => {
              const upper = name.toUpperCase()
              return upper in replacements ? replacements[upper] : match
            })
          : v
    }
    currentRef.current = replaced
  }

  return currentRef.current
}
